import { createSelector } from 'reselect';

import { environment } from '../../../environments/environment';
import { DisplayNamePipe } from '../../shared/pipes/display-name.pipe';
import { auth, getStatisticsState, statistics, getAll, CHECK_INS, getIds } from '../reducers';
import { getActiveCustomers, getActiveMembers } from './statistics.selectors';

import * as moment from 'moment-timezone';

const MEMO_NAMES = {
  low: 'Postcard',
  mid: 'Note',
  high: 'Flag',
  severe: 'Alert'
};

export const SORT_DATE_FORMAT = 'YYYYMMDD';

/**
 * Returns the popular memo types suitable for passing to the leaderboard component
 */
export const popularMemoTypesTable = createSelector(getStatisticsState, state => {
  return state.memos && state.memos.data
    ? Object.getOwnPropertyNames(state.memos.data.types)
        .map(type => ({
          name: MEMO_NAMES[type],
          icon: `ca-priority-${type}`,
          score: state.memos.data.types[type]
        }))
        .filter(type => type.score > 0)
        .sort((a, b) => {
          if (a.score < b.score) return 1;
          if (a.score > b.score) return -1;
          return 0;
        })
    : [];
});

export const getMemoCount = createSelector(statistics.getMemoStats, stats => {
  if (!stats.data) {
    return null;
  }
  return stats.data.total;
});

/**
 * Returns the list of active members suitable for passing to the leaderboard component
 */
export const activeMembersTable = createSelector(getActiveMembers, stats => {
  const namePipe = new DisplayNamePipe();
  const version = environment.API_VERSION;
  const url = environment.API_BASE_URL;

  return stats.slice(0, 5).map(stat => ({
    name: namePipe.transform(stat.member),
    scores: [stat.memoCreated || 0, stat.checkedIn || 0],
    avatarUrl:
      stat.member && stat.member.profileImage
        ? `${url}/${version}/files/${stat.member.profileImage.id}/download`
        : ''
  }));
});

/**
 * Returns the list of active customers suitable for passing to the leaderboard component
 */
export const activeCustomersTable = createSelector(
  getActiveCustomers,
  auth.getAuthToken,
  (stats, token) => {
    const namePipe = new DisplayNamePipe();
    const version = environment.API_VERSION;
    const url = environment.API_BASE_URL;

    return stats.slice(0, 5).map(stat => ({
      name: namePipe.transform(stat.customer),
      scores: [stat.memoCreated || 0, stat.checkedIn || 0, stat.assignedTeamMembers || 0],
      avatarUrl:
        stat.customer && stat.customer.profileImage
          ? `${url}/${version}/files/${
              stat.customer.profileImage.id
            }/download?access_token=${token}`
          : ''
    }));
  }
);

export const checkInScoreTally = createSelector(getAll(CHECK_INS), checkIns => {
  return (<Array<any>>checkIns).reduce(
    (accum, checkIn) => {
      switch (checkIn.score) {
        case 0:
          accum.poor++;
          return accum;
        case 1:
          accum.fair++;
          return accum;
        case 2:
          accum.good++;
          return accum;
        case 3:
          accum.excellent++;
          return accum;
        default:
          return accum;
      }
    },
    { excellent: 0, good: 0, fair: 0, poor: 0 }
  );
});

export const groupCheckInsByDate = createSelector(getAll(CHECK_INS), checkIns => {
  return (<Array<any>>checkIns).reduce((accum, checkIn) => {
    const date = moment(checkIn.created).format(SORT_DATE_FORMAT);
    if (accum[date]) {
      accum[date].push(checkIn);
    } else {
      accum[date] = [checkIn];
    }
    return accum;
  }, {});
});

export const checkInCount = createSelector(getIds(CHECK_INS), ids => (<Array<string>>ids).length);

export const checkInTallyByDate = createSelector(groupCheckInsByDate, checkIns => {
  return Object.getOwnPropertyNames(checkIns).reduce((final, date) => {
    final[date] = checkIns[date].reduce(
      (accum, checkIn) => {
        switch (checkIn.score) {
          case 0:
            accum.poor++;
            return accum;
          case 1:
            accum.fair++;
            return accum;
          case 2:
            accum.good++;
            return accum;
          case 3:
            accum.excellent++;
            return accum;
          default:
            return accum;
        }
      },
      { excellent: 0, good: 0, fair: 0, poor: 0 }
    );
    return final;
  }, {});
});

export const checkInTalliesForDateRange = createSelector(
  checkInTallyByDate,
  statistics.getFilters,
  (checkIns, filters) => {
    let all = {};
    for (let d = moment(filters.dateFrom); d.isBefore(filters.dateTo); d.add(1, 'days')) {
      all[d.format(SORT_DATE_FORMAT)] = checkIns[d.format(SORT_DATE_FORMAT)];
    }
    return all;
  }
);
